import type { Joint } from "../../types/scene";
import { logger } from "../../utils/logger";
import { isScalarJoint } from "./xmlGeneratorConfig";
import type { ExportBodyNode, JointRefForExport } from "./xmlGenerationTypes";

export const buildInitScalarQposByJointUuid = ({
  bodyTreeForExport,
  firstStoredKeyframeQpos,
  hasBodyConnectEqualities,
  jointMap,
}: {
  bodyTreeForExport: ExportBodyNode[];
  firstStoredKeyframeQpos: number[] | null;
  hasBodyConnectEqualities: boolean;
  jointMap: Record<string, Joint>;
}): Record<string, number> => {
  const initScalarQposByJointUuid: Record<string, number> = {};
  if (!hasBodyConnectEqualities || !firstStoredKeyframeQpos) {
    return initScalarQposByJointUuid;
  }

  try {
    const initIndexRef = { current: 0 };
    const resolveJointRefForInit = (entry: JointRefForExport): Joint | null => {
      if (!entry) return null;
      if (typeof entry === "string") {
        return (jointMap[entry as import("../../types/scene").JointUUID] as Joint) || null;
      }
      const uuid = entry.uuid ?? entry.name;
      return uuid
        ? (jointMap[uuid as import("../../types/scene").JointUUID] as Joint) || null
        : null;
    };
    const indexBody = (node: ExportBodyNode) => {
      const bodyJointRefs = Array.isArray(node.joints) ? node.joints : [];
      bodyJointRefs
        .map((ref) => resolveJointRefForInit(ref))
        .filter((j): j is Joint => !!j)
        .forEach((j) => {
          if (j.kind === "free") {
            initIndexRef.current += 7;
            return;
          }
          if (j.kind === "ball") {
            initIndexRef.current += 4;
            return;
          }
          if (isScalarJoint(j)) {
            const value = firstStoredKeyframeQpos[initIndexRef.current];
            if (Number.isFinite(Number(value))) {
              initScalarQposByJointUuid[String(j.uuid)] = Number(value);
            }
            initIndexRef.current += 1;
          }
        });
      node.children?.forEach(indexBody);
    };
    bodyTreeForExport.forEach(indexBody);
  } catch (error) {
    logger.warn(
      "[XML Gen] Failed to index init keyframe scalar refs for body equality qpos0:",
      error
    );
  }

  return initScalarQposByJointUuid;
};
